import { useCallback, useState, useMemo } from 'react'
import { CopyDialog, DeleteConfirmDialog } from '@library'
import { DataGrid, GridColDef } from '@microservices/wiskey-react-table-component'
import { useMobileGlobalVariables } from '@mobileHooks'
import { Variable } from '@mobileTypes'
import { Button, Typography, Paper, Box, Chip } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { generateDataGridActionsColumn } from '@helpers'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { PageTitle } from '@components/PageTitle'

import { VariableCreateDialog } from '@mobileComponents/Variables'

export const ConfigMobileGlobalVariables = () => {
  const {
    variables,
    loading,
    createGlobalVariable,
    updateGlobalVariable,
    deleteGlobalVariable,
    copyGlobalVariable,
  } = useMobileGlobalVariables()

  const [dialogState, setDialogState] = useState<{
    open: boolean
    mode: 'create' | 'edit'
    initialData?: Variable
  }>({
    open: false,
    mode: 'create',
  })

  const handleAdd = useCallback(() => {
    setDialogState({
      open: true,
      mode: 'create',
    })
  }, [])

  const handleEdit = useCallback((variable: Variable) => {
    setDialogState({
      open: true,
      mode: 'edit',
      initialData: variable,
    })
  }, [])

  const handleDelete = useCallback(
    (id: string) => {
      deleteGlobalVariable(id)
    },
    [deleteGlobalVariable]
  )

  const handleCopy = useCallback(
    (id: string, newName: string) => {
      copyGlobalVariable(id, newName)
    },
    [copyGlobalVariable]
  )

  const handleSave = useCallback(
    (variable: Variable, _isGlobal: boolean) => {
      if (dialogState.mode === 'create') {
        createGlobalVariable(variable)
      } else if (dialogState.mode === 'edit' && dialogState.initialData) {
        updateGlobalVariable(dialogState.initialData.internalId, variable)
      }
      setDialogState({ open: false, mode: 'create' })
    },
    [dialogState, createGlobalVariable, updateGlobalVariable]
  )

  const handleClose = useCallback(() => {
    setDialogState({ open: false, mode: 'create' })
  }, [])

  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [itemToCopy, setItemToCopy] = useState<Variable | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<Variable | null>(null)

  const handleCopyClick = useCallback((variable: Variable) => {
    setItemToCopy(variable)
    setCopyDialogOpen(true)
  }, [])

  const handleCopyConfirm = useCallback(
    (newInternalId: string) => {
      if (itemToCopy) {
        handleCopy(itemToCopy.internalId, newInternalId)
        setCopyDialogOpen(false)
        setItemToCopy(null)
      }
    },
    [itemToCopy, handleCopy]
  )

  const handleDeleteClick = useCallback((variable: Variable) => {
    setItemToDelete(variable)
    setDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (itemToDelete) {
      handleDelete(itemToDelete.internalId)
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    }
  }, [itemToDelete, handleDelete])

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'internalId',
        headerName: 'Internal ID',
        flex: 1,
        minWidth: 150,
        display: 'flex',
        justifyContent: 'center',
        renderCell: params => (
          <Typography variant='body2' fontWeight='medium'>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'variableType',
        headerName: 'Variable Type',
        flex: 1,
        minWidth: 120,
        renderCell: params => (
          <Chip
            label={params.value}
            size='small'
            color={params.value === 'multiple' ? 'secondary' : 'default'}
          />
        ),
      },
      {
        field: 'valueType',
        headerName: 'Value Type',
        flex: 1,
        minWidth: 120,
        renderCell: params => <Chip label={params.value} size='small' variant='outlined' />,
      },
      {
        field: 'persistent',
        headerName: 'Persistent',
        flex: 1,
        minWidth: 120,
        renderCell: params =>
          params.value ? (
            <Chip label='Yes' size='small' variant='outlined' />
          ) : (
            <Chip label='No' size='small' variant='outlined' />
          ),
      },
      {
        field: 'objectCode',
        headerName: 'Object Code',
        flex: 1,
        minWidth: 150,
        display: 'flex',
        justifyContent: 'center',
        renderCell: params => (
          <Typography
            variant='body2'
            color={params.value ? 'primary' : 'text.secondary'}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'defaultValue',
        headerName: 'Default Value',
        flex: 1,
        minWidth: 200,
        display: 'flex',
        justifyContent: 'center',
        renderCell: params => (
          <Typography
            variant='body2'
            sx={{
              fontFamily: 'monospace',
              fontSize: 11,
              color: params.value !== undefined && params.value !== null ? 'text.primary' : 'text.secondary',
            }}
          >
            {params.value !== undefined && params.value !== null
              ? JSON.stringify(params.value)
              : undefined}
          </Typography>
        ),
      },
      generateDataGridActionsColumn({
        onEdit: params => {
          const variable = variables.find(v => v.internalId === params.row.internalId)
          if (variable) {
            handleEdit(variable)
          }
        },
        onCopy: params => {
          const variable = variables.find(v => v.internalId === params.row.internalId)
          if (variable) {
            handleCopyClick(variable)
          }
        },
        onDelete: params => {
          const variable = variables.find(v => v.internalId === params.row.internalId)
          if (variable) {
            handleDeleteClick(variable)
          }
        },
      }),
    ],
    [variables, handleEdit, handleCopyClick, handleDeleteClick]
  )

  return (
    <>
      <PageContentLayout sx={{ p: 1 }}>
        <PageTitle
          start={
            <>
              <Typography variant='h6'>Global Variables</Typography>
              <Button variant='contained' startIcon={<AddIcon />} onClick={handleAdd}>
                Add Variable
              </Button>
            </>
          }
          sx={{ mb: 2 }}
        />

        {variables.length === 0 && !loading ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              No variables configured. Click "Add Variable" to create one.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flex: 1, height: 400 }}>
            <DataGrid
              rows={variables}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              hideFooter
              getRowId={row => row.internalId}
            />
          </Box>
        )}

        <VariableCreateDialog
          open={dialogState.open}
          mode={dialogState.mode}
          initialData={dialogState.initialData}
          existingVariables={variables.filter(v => v.internalId !== dialogState.initialData?.internalId)}
          showValueTypeSelector
          showVariableTypeSelector
          showDefaultValue
          showObjectCodeSelector
          showPersistentToggle
          onClose={handleClose}
          onSave={handleSave}
        />

        <CopyDialog
          open={copyDialogOpen}
          title='Copy Global Variable'
          initialName={itemToCopy ? `${itemToCopy.internalId} (copy)` : ''}
          existingNames={variables.map(v => v.internalId)}
          onClose={() => {
            setCopyDialogOpen(false)
            setItemToCopy(null)
          }}
          onCopy={handleCopyConfirm}
        />

        <DeleteConfirmDialog
          open={deleteDialogOpen}
          title='Delete Global Variable'
          message='Are you sure you want to delete global variable'
          itemName={itemToDelete?.internalId}
          onClose={() => {
            setDeleteDialogOpen(false)
            setItemToDelete(null)
          }}
          onConfirm={handleDeleteConfirm}
        />
      </PageContentLayout>
    </>
  )
}
